import { Status, VerifierType } from "./FormEnums";

export interface iUserDTO {
  id: number | null;
  firstName: string;
  lastName: string;
  last4: string | null;
  email: string;
  phone: string;
  authId: string;
  ssn: string | null;
  dob: string | null;
  smsConsent: boolean;
  ssnDobSet: boolean;
  studentSchools: iStudentSchoolDTO[];
  mfaOptions: iKeycloakOption[];
}

export interface iUserServiceHistoryDTO {
  id: number;
  source: string | null;
  type: number;
  description: string | null;
  body: string | null;
  action_Date: string | null;
  commenter_Name: string | null;
  commenter_Id: number | null;
}

export interface iUserServiceHistoryCommentDTO {
  id: number;
  auth_Id: string;
  commenter_Id: number;
  submission_Forms_Id: number;
  submission_Forms_Uuid: string;
  created_On: string;
  comment: string;
}

export interface iKeyCloakUserRequestDTO {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  orgId: string;
  groups: string[];
  productIdList: number[];
}

export interface iStudentSchoolDTO {
  id: number | null;
  user_Id: number | null;
  school_Student_Id: string | null;
  school_Name: string | null;
  school_Id: number | null;
  purl: string | null;
}

export interface iOrgDTO {
  id: number;
  org_Type_Id: number;
  name: string;
  city: string | null;
  state: string | null;
  address: string | null;
  address2: string | null;
  zip: string | null;
  opeid: string;
  source_Id: number;
  last_Update_Date: Date;
  add_Date: Date;
  purl: string;
}

export interface iProductDTO {
  id: number;
  name: string;
  code: string;
}

export interface iSubmission {
    id: number | null;
    uuid: string | undefined;
    formVersionId: number;
    orgId: number;
    submissionTypeId: number | null;
    statusId: number | null;
    //this field is never used in an update, only in a create, so I'm going to use it in the API to determine if the logged in user is a contributor or not
    submittedBy: number;
    updatedBy: number | null;
    submissionJson: string;
    documents: iDocumentDTO[];
    isRetakingForm: boolean;
    isSubmittingForm: boolean;
}

export interface iTagDTO {
  id: number;
  tag_Name: string;
  org_Id: number;
}

export interface iAdminTag {
  id: number;
  tag_Name: string;
  forms_Using: number;
  submissions_Using: number;
}

export interface iFormTag {
  id: number;
  tag_Name: string;
  org_Id: number;
  formId: number;
}

export interface iFormsInvoiceStatusDTO {
  id: number;
  formId: number;
  status_Id: number;
  created_On: string;
}

export interface iDocumentDTO {
  id: number;
  uuid: string;
  file_Type_Id: number | null;
  file_Path: string | null;
  file_Name: string;
  original_File_Name: string;
  upload_Date: string | null;
  user_Id: number;
}

export interface iTodo {
  formId: number;
  formUuid: string;
  formVersionId: number;
  formVersionUuid: string;
  orgId: number;
  organization: string | null;
  name: string;
  description: string | null;
  priorityId: number;
  statusId: number | null;
  dueDate: string | null;
  endDate: string | null;
  formSubmissionId: number | null;
  formSubmissionUuid: string;
  json: string;
  canRetake: boolean;
  ownedBy: number;
  ownedByEmail: string;
}

export interface iUserLink {
  Id: number;
  userId?: number | null;
  contributorId?: number | null;
  contributorName?: string | null;
  contributorEmail?: string | null;
  userLinkUUID: string;
  userName?: string | null;
  userEmail?: string | null;
  status: string;
  statusDate?: Date | null;
  createdOn: Date;
  userIsContributor: boolean;
  emailSentTo: string; 
}

export interface iBinderRequest {
  opeId: string;
  formUuid: string;
  requestedBy: string;
  submissionUuids: string[];
  downloadNow?: boolean;
}
export interface iBinder {
  id: number;
  uuid: string;
  orgId: number;
  requestedBy: string;
  binderStatusId: number;
  statusDate: string;
  createdOn: string;
  documentUuid: string;
  fileName: string | null;
  form: string;
}

export class AdminRoleNodes {
  admin?: React.ReactNode | null;
  institutionAdmin?: React.ReactNode | null;
  institution?: React.ReactNode | null;
  srOperations?: React.ReactNode | null;
  operations?: React.ReactNode | null;
  manager?: React.ReactNode | null;
}

export class iKeycloakOption {
  id: string = ""
  type: string = ""
}

export interface iPendingFinalReviewRecord {
  submissionFormId?: number;
  form?: string;
  submittedBy?: string;
  assignedTo?: string;
  sentToQc?: string;
  statusAfterQc?: string;
  qcCompleted?: string;
  currentStatus?: string;
}

export interface iPendingFinalReviewSearch {
  opeId?: string;
  form?: string;
  submittedBy?: string;
  assignedTo?: string;
  statusAfterQc?: Status | number;
  sentToQcStartDate?: Date | string;
  sentToQcEndDate?: Date | string;
  qcCompletedStartDate?: Date | string;
  qcCompletedEndDate?: Date | string;
}

export interface iProductivityRecord {
  submissionFormId?: number;
  form?: string;
  submittedBy?: string;
  assignedTo?: string;
  sentToInReview?: string;
  statusAfterInReview?: string;
  reviewCompleted?: string;
  currentStatus?: string;
}

export interface iProductivityRecordSearch {
  opeId?: string;
  form?: string;
  submittedBy?: string;
  assignedTo?: string;
  statusAfterInReview?: Status | number;
  sentToInReviewStartDate?: Date | string;
  sentToInReviewEndDate?: Date | string;
  reviewCompletedStartDate?: Date | string;
  reviewCompletedEndDate?: Date | string;
}

export interface iFormDTO {
  id: number;
  uuid: string | null;
  orgId: number;
  orgName?: string | null;
  name: string | null;
  description: string | null;
  submittedBy: number;
  createdOn: Date | string;
  lastUpdated: Date | string;
  json?: string | null;
  verifierTypeId: VerifierType | null;
  academicAidYearId: number | null;
  dueDate: Date | string | null;
  hasSchoolFinalReview: boolean | null;
  schoolReviewEmail: boolean | null;
}

export function mapAdminTagToTag(tag: iAdminTag, orgId: number): iTagDTO {
  return {
    id: tag.id,
    tag_Name: tag.tag_Name,
    org_Id: orgId
  };
}